import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { resolveSmartLinkPlatformDomain } from 'fym-shared/src/smartlinks/domains';
import { TypedMemo } from '../../common/types';
import { useGetAccountQuery, useGetSmartLinkQuery } from '../../../redux/api/fymAPI';
import { openExternal } from '../../../utils/openExternal';
import { removeHttpAndSlash } from '../utils/removeHttpRegex';
import { applyPlatformDomainToPrefix } from '../utils/platformDomainPrefix';
import { useQueryParam } from '../../../utils/hooks/useQueryParam';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { SMLPublishingModal } from '../../common/views/modals/smlPublishingModal/SMLPublishingModal';
import { useShowToast } from '../../../redux/slices/toasts/hooks';
import { ToastType } from '../../../redux/slices/toasts/state';
import { SMLEditor } from '../editor/SMLEditor';
import { editorMessages } from '../editor/editorMessages';
import { useLegacyEditorRedirect } from './useLegacyEditorRedirect';
import { isSmartLinkUnavailableError, SmartLinkPageIds, smartLinkPageIdsOf } from '../unavailable/smartLinkPageIds';
import { SmartLinkUnavailableView } from '../unavailable/SmartLinkUnavailableView';
import { usePreviewPageLang } from '../smlPreview/usePreviewPageLang';

interface SmartLinkDetailsViewProps {
  route?: {
    params: {
      accountId: number;
      smartLinkId: number;
    };
  };
}

const SmartLinkDetailsContent: React.FC<SmartLinkPageIds> = ({ accountId, smartLinkId }) => {
  const intl = useIntl();
  const withLang = usePreviewPageLang();
  const router = useSMLRouter();
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  useLegacyEditorRedirect(accountId, smartLinkId);

  const { data, isLoading, error, refetch } = useGetSmartLinkQuery({ accountId, smartLinkId });
  const smartLink = data?.smartLink;
  const { show: showToast } = useShowToast();

  /**
   * The first publish opens the sheet; every one after it gets a toast (design a4, 2). The sheet
   * hands over an address the artist has never seen; a later publish only confirms itself.
   */
  const onPublished = useCallback(
    (isFirstPublish: boolean) => {
      if (isFirstPublish) {
        setModalOpen(true);
        return;
      }
      showToast({
        type: ToastType.success,
        description: intl.formatMessage(editorMessages.publishDone),
        linkUrl: smartLink?.shareUrl ?? undefined,
        autoHide: true,
      });
    },
    [intl, showToast, smartLink?.shareUrl]
  );
  const closeModal = useCallback(() => setModalOpen(false), []);
  const goBack = useCallback(() => router.push(SMLRoutes.smartlinks), [router]);

  // The page's public host, shown before the slug. A page may sit on another host than the account.
  const { data: accountResponse } = useGetAccountQuery({ accountId });
  const accountSlugPrefix = `${removeHttpAndSlash(accountResponse?.account.shareUrl ?? 'slug.fym.fm')}/`;
  const platformDomain = resolveSmartLinkPlatformDomain(
    smartLink?.platformDomain,
    accountResponse?.account.platformDomain
  );
  const slugPrefix = useMemo(
    () => applyPlatformDomainToPrefix(accountSlugPrefix, platformDomain),
    [accountSlugPrefix, platformDomain]
  );

  const openPage = useCallback(() => {
    // A live page opens at its real address; a draft opens the previewToken url of the draft.
    const isLive = smartLink?.publishedSmartLinkVersion !== null && smartLink?.publishedSmartLinkVersion !== undefined;
    const url = isLive
      ? (smartLink?.shareUrl ?? smartLink?.recentSmartLinkVersion.previewUrl)
      : (smartLink?.recentSmartLinkVersion.previewUrl ?? smartLink?.shareUrl);
    if (url === null || url === undefined) {
      return;
    }
    openExternal(withLang(url)).catch(console.error);
  }, [smartLink, withLang]);

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);
  if (isSmartLinkUnavailableError(error)) {
    return <SmartLinkUnavailableView />;
  }
  if (error !== undefined) {
    return <ErrorView error={error} retry={retry} />;
  }
  if (smartLink === undefined || isLoading) {
    return <LoadingView />;
  }

  const name = smartLink.isMainSmartLink
    ? intl.formatMessage(editorMessages.bioLinkName)
    : smartLink.recentSmartLinkVersion.name;

  return (
    <>
      <SMLPublishingModal closeModal={closeModal} isOpen={isModalOpen} accountId={accountId} smartLink={smartLink} />
      <SMLEditor
        smartLink={smartLink}
        name={name}
        slugPrefix={slugPrefix}
        artistName={accountResponse?.account.name ?? null}
        onBack={goBack}
        onOpenPage={openPage}
        onPublished={onPublished}
      />
    </>
  );
};

/**
 * The SmartLink editor route (`/details?accountId=&smartLinkId=`, design smartlink-editor). It loads
 * the page and the account address, and hands both to the editor.
 */
const SmartLinkDetailsViewComponent: React.FC<SmartLinkDetailsViewProps> = ({ route }) => {
  // A missing or bad id (`accountId=abc`) shows the "not available" state (QA finding brk-2).
  const ids = smartLinkPageIdsOf(useQueryParam('accountId', route), useQueryParam('smartLinkId', route));
  if (ids === null) {
    return <SmartLinkUnavailableView />;
  }
  return <SmartLinkDetailsContent accountId={ids.accountId} smartLinkId={ids.smartLinkId} />;
};

export const SmartLinkDetailsView = TypedMemo(SmartLinkDetailsViewComponent);
